import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {
  DASHBOARD_POLLING_INTERVAL_DEFAULT_SECONDS,
  DASHBOARD_POLLING_INTERVAL_MAX_SECONDS,
  DASHBOARD_POLLING_INTERVAL_MIN_SECONDS
} from '@constants/dashboard-polling-interval.constant';
import {DashboardLayouts} from '@models/dashboard/dashboard-layouts';
import {DashboardMachine} from '@models/dashboard/dashboard-machine';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {DashboardService} from '@services/dashboard/dashboard.service';
import {Organization} from '@models/organization/organization';
import {User} from '@models/user/user';
import {Observable} from 'rxjs';
import {Role} from '@models/user/role';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {
  MachineCategoryColumnConfiguration
} from '@models/adminstration/portal-setup/machine-category/machine-category-column-configuration';
import {switchMap} from 'rxjs/operators';
import {SettingsService} from '@services/administration/settings/settings.service';
import {OrganizationService} from '@services/administration/organization/organization.service';

@Component({
    selector: 'scitis-machine-tile-container',
    templateUrl: './machine-tile-container.component.html',
    styleUrls: ['./machine-tile-container.component.scss'],
    standalone: false
})
export class MachineTileContainerComponent implements OnInit, OnChanges {

  @Input()
  dashboardLayout: string = '';

  @Input()
  currentLocation: string = '';
  @Input()
  currentMachineGroupId: string = '';
  @Input()
  machineCount: number = 0;

  @Input()
  user: User | undefined;
  @Input()
  isWindowContentVisible: boolean = true;
  @Input()
  availableMachineCategories: MachineCategoryColumnConfiguration[] = [];
  public shownMachines: Set<DashboardMachine> = new Set<DashboardMachine>();
  public isSuperAdminRole: boolean = false;
  public isAdminRole: boolean = false;
  public isAnalyticsRole: boolean = false;
  public isViewerRole: boolean = false;
  public refreshInterval: number = -1;

  private organizationId: number = -1;
  private rootMachineryGroupId: string = '';
  private currentPageNumber: number = 1;
  private numberOfItemsPerPage: number = 15;
  private whiteListedMachineryGroups: null | number[] = null;

  constructor(private loadingIndicator: LoadingIndicatorService,
              private dashboardService: DashboardService,
              private organizationService: OrganizationService,
              private notificationService: NotificationService,
              private settingsService: SettingsService,
              private translateService: TranslateService) {
  }

  public get moreMachinesAreAvailable(): boolean {
    return this.currentPageNumber * this.numberOfItemsPerPage < this.machineCount;
  }

  public get isTileLayout(): boolean {
    return this.dashboardLayout == DashboardLayouts.TILE;
  }

  ngOnInit(): void {
    this.getUserDetails(this.user);
    this.getRefreshInterval();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes) {
      return;
    }

    if (changes.user && changes.user.currentValue) {
      this.getUserDetails(changes.user.currentValue);
      this.getRefreshInterval();
    }
    if (changes.currentLocation && changes.currentLocation.currentValue) {
      this.currentPageNumber = 1;
    }
    if (changes.currentLocation || changes.currentMachineGroupId) {
      this.getDashboardDetailsFromElasticSearch(this.currentMachineGroupId, this.user);
    }
  }

  public onScrollDown() {
    this.onLoadMoreMachines();
  }

  private onLoadMoreMachines() {
    this.loadingIndicator.show();
    this.currentPageNumber++;
    let machineryGroupId: string = this.currentMachineGroupId;
    if (!machineryGroupId || machineryGroupId.isNullOrWhitespace()) {
      machineryGroupId = this.rootMachineryGroupId;
    }
    const machinesObservable = this.dashboardService.getDashboardTileMachinesFromElastic(machineryGroupId, this.organizationId, this.currentLocation, this.currentPageNumber, this.numberOfItemsPerPage, this.whiteListedMachineryGroups);
    machinesObservable.subscribe({
      next: (success: Set<DashboardMachine>) => {
        this.shownMachines = new Set([...this.shownMachines, ...success]);
        this.loadingIndicator.hide();
      }, error: (error) => {
        const message = this.translateService.instant('There was a problem loading more machines  of group') + ' ' + machineryGroupId;
        this.notificationService.error(message, error, 'MachineTileContainer', true);
        this.loadingIndicator.hide();
      }
    });
  }

  private getUserDetails(user: User | undefined) {
    if (!user) {
      return;
    }
    const userRole = new Role(user);
    this.isSuperAdminRole = userRole.isSuperAdminRole || userRole.isTechAdminRole;
    this.isAdminRole = userRole.isAdminRole;
    this.isAnalyticsRole = userRole.isAnalyticsRole;
    this.isViewerRole = userRole.isViewerRole;
    this.whiteListedMachineryGroups = userRole.isSuperAdminRole ? null : user.getWhiteListedMachineryGroups(true);

    this.organizationId = userRole.isSuperAdminRole ? -1 : user.parentId;
    this.rootMachineryGroupId = String(user.machineGroup);
  }

  private getRefreshInterval(): void {
    const organizationId = this.user?.parentId;
    if (!organizationId || organizationId <= 0) {
      this.getGlobalRefreshInterval();
      return;
    }

    this.organizationService.getOrganizationDetails(organizationId).subscribe({
      next: (organization: Organization) => {
        const configuredInterval = Number(organization?.dashboardListPollingIntervalInSeconds);
        this.refreshInterval = this.isValidRefreshInterval(configuredInterval)
          ? configuredInterval
          : DASHBOARD_POLLING_INTERVAL_DEFAULT_SECONDS;
      },
      error: () => this.getGlobalRefreshInterval(),
    });
  }

  private getGlobalRefreshInterval(): void {
    this.dashboardService.getDashboardListRefreshInterval().subscribe({
      next: (result) => {
        const configuredInterval = Number(result?.value);
        this.refreshInterval = this.isValidRefreshInterval(configuredInterval)
          ? configuredInterval
          : DASHBOARD_POLLING_INTERVAL_DEFAULT_SECONDS;
      },
      error: () => {
        this.refreshInterval = DASHBOARD_POLLING_INTERVAL_DEFAULT_SECONDS;
      },
    });
  }

  private isValidRefreshInterval(intervalInSeconds: number): boolean {
    return Number.isFinite(intervalInSeconds)
      && intervalInSeconds >= DASHBOARD_POLLING_INTERVAL_MIN_SECONDS
      && intervalInSeconds <= DASHBOARD_POLLING_INTERVAL_MAX_SECONDS;
  }

  private getDashboardDetailsFromElasticSearch(groupId: string, user: User | undefined): void {
    if (!user) {
      return;
    }
    this.loadingIndicator.show();
    const machineryGroupId: string = this.getValidMachineryGroupId(groupId);
    this.getMachinesFromElastic(machineryGroupId, this.organizationId);
  }

  private getValidMachineryGroupId(groupId: string): string {
    let machineryGroupId: string = groupId;
    if (!machineryGroupId || machineryGroupId.isNullOrWhitespace()) {
      machineryGroupId = this.rootMachineryGroupId;
    }
    return machineryGroupId;
  }

  private getMachinesFromElastic(machineryGroupId: string, organizationId: number): Observable<any> {
    const machinesObservable = this.settingsService.isLiveDataStreamingEnabled()
    .pipe(switchMap(() => {
      return this.dashboardService.getDashboardTileMachinesFromElastic(machineryGroupId, organizationId, this.currentLocation, this.currentPageNumber, this.numberOfItemsPerPage, this.whiteListedMachineryGroups);
    }));

    machinesObservable.subscribe({
      next: (success: Set<DashboardMachine>) => {
        this.shownMachines = success;
        this.loadingIndicator.hide();
      }, error: (error) => {
        const message = this.translateService.instant('There was a problem loading machines of group') + ' ' + machineryGroupId;
        this.notificationService.error(message, error, 'MachineTileContainer', true);
        this.loadingIndicator.hide();
      }
    });
    return machinesObservable;
  }
}
